/**
 * Deals: technologies, gold, gold a turn, open borders, alliances (ADR-0060) and
 * strategic resources (ADR-0063).
 *
 * A deal is two lists — what the proposer gives and what it asks for — between two
 * civilisations at peace that have met. Everything about one lives here: whether it is
 * legal, what an AI civilisation thinks it is worth, and carrying it out. The AI judges
 * the player's offers with exactly the function it judges its own by, so a refusal
 * always has a reason, and the reason is shown.
 */

import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { knows } from '../research/knowledge.js';
import { techCost } from '../research/index.js';
import { NONE, type Civ, type DealItem, type GameState, type Id } from '../state/types.js';
import { weightsFor } from '../ai/personality.js';
import { attitude } from './attitude.js';
import { inContact } from './borders.js';
import { atWar, declareWar, militaryStrength, treatyLeft, truceLeft } from './index.js';
import { damageReputation, remember } from './memory.js';
import { ensureRelation, relationOf } from './relation.js';
import { resourceAccess } from '../resources/access.js';
import { resourceKnown, resourceUsed } from '../resources/known.js';
import { techBlocker } from '../cities/production.js';
import { citiesOf } from '../cities/index.js';
import { budget, isCapital } from '../cities/economy.js';
import { cedeCity } from '../combat/conquest.js';

export type DealRefusal = 'NoSuchCiv' | 'NotMet' | 'AtWarWith' | 'EmptyDeal' | 'BadDealItem' | 'Truce';

const MUTUAL = new Set(['openBorders', 'alliance']);

function itemKey(item: DealItem): string {
  switch (item.kind) {
    case 'tech':
      return `tech:${item.tech}`;
    case 'gold':
      return 'gold';
    case 'goldPerTurn':
      return 'goldPerTurn';
    case 'openBorders':
      return 'openBorders';
    case 'alliance':
      return `alliance:${item.against}`;
    case 'resource':
      return `resource:${item.resource}`;
    case 'city':
      return `city:${item.city}`;
  }
}

/** Whether `giver` can hand `item` to `receiver`. Mutual items are judged for the pair. */
export function itemValid(state: GameState, content: Content, giver: Civ, receiver: Civ, item: DealItem): DealRefusal | null {
  switch (item.kind) {
    case 'city': {
      // Never the seat of government, nor a civilisation's last city (ADR-0071).
      const city = state.cities[item.city];
      if (!city || city.owner !== giver.id || isCapital(content, city)) return 'BadDealItem';
      return citiesOf(state, giver.id).length > 1 ? null : 'BadDealItem';
    }
    case 'tech': {
      if (!Number.isInteger(item.tech) || item.tech < 0 || item.tech >= content.technologies.all.length) return 'BadDealItem';
      if (!knows(giver, item.tech) || knows(receiver, item.tech)) return 'BadDealItem';
      // Knowledge only lands on what it is built on, or the tree would float.
      if (!content.technologies.get(item.tech).prerequisites.every((p) => knows(receiver, p))) return 'BadDealItem';
      return null;
    }
    case 'gold':
      return Number.isInteger(item.amount) && item.amount > 0 && item.amount <= giver.treasury ? null : 'BadDealItem';
    case 'goldPerTurn':
      return Number.isInteger(item.amount) && item.amount > 0 && item.amount <= 999 ? null : 'BadDealItem';
    case 'openBorders':
      return relationOf(state, giver.id, receiver.id)?.openBorders ? 'BadDealItem' : null;
    case 'alliance': {
      const enemy = state.civs[item.against];
      if (!enemy || enemy.defeated || item.against === giver.id || item.against === receiver.id) return 'BadDealItem';
      if (!inContact(state, giver.id, item.against) || !inContact(state, receiver.id, item.against)) return 'BadDealItem';
      if (relationOf(state, giver.id, receiver.id)?.alliances.includes(item.against)) return 'BadDealItem';
      if (truceLeft(state, giver.id, item.against) > 0 || truceLeft(state, receiver.id, item.against) > 0) return 'Truce';
      return null;
    }
    case 'resource': {
      // Only a resource the giver draws from its own land, which the receiver can
      // see and does not already have.
      if (!Number.isInteger(item.resource) || item.resource <= 0 || item.resource >= content.resources.all.length) return 'BadDealItem';
      if (content.resources.get(item.resource).class !== 'strategic' || !resourceUsed(content, item.resource)) return 'BadDealItem';
      const bit = 1 << item.resource;
      if ((resourceAccess(state, content, giver.id).own & bit) === 0) return 'BadDealItem';
      if (!resourceKnown(content, receiver, item.resource)) return 'BadDealItem';
      const theirs = resourceAccess(state, content, receiver.id);
      if (((theirs.own | theirs.traded) & bit) !== 0) return 'BadDealItem';
      return null;
    }
  }
}

/** Why `from` cannot offer this deal to `to`, or null. */
export function dealBlocker(state: GameState, content: Content, from: Id, to: Id, give: DealItem[], get: DealItem[]): DealRefusal | null {
  const a = state.civs[from];
  const b = state.civs[to];
  if (!a || !b || a.defeated || b.defeated || from === to) return 'NoSuchCiv';
  if (!inContact(state, from, to)) return 'NotMet';
  if (atWar(state, from, to)) return 'AtWarWith';
  if (give.length + get.length === 0) return 'EmptyDeal';
  const seen = new Set<string>();
  for (const [list, giver, receiver] of [
    [give, a, b],
    [get, b, a],
  ] as const) {
    for (const item of list) {
      // Cities change hands only as a term of peace (ADR-0071).
      if (item.kind === 'city') return 'BadDealItem';
      const key = MUTUAL.has(item.kind) ? itemKey(item) : `${giver.id}:${itemKey(item)}`;
      if (seen.has(key)) return 'BadDealItem';
      seen.add(key);
      const refused = itemValid(state, content, giver, receiver, item);
      if (refused) return refused;
    }
  }
  return null;
}

export interface Appraisal {
  /** What the judge gains and gives up, in gold. */
  gain: number;
  loss: number;
  accept: boolean;
  /** Why, in a phrase the player can read. */
  reason: string;
  /** Gold more that would make it acceptable, when that is all that is missing. */
  shortfall: number;
}

/**
 * Whether a civilisation could keep paying this much gold a turn on top of what it already
 * owes (ADR-0073): no more than `gptIncomeShare` of what it would have spare with no
 * science at all.
 */
export function canPromise(state: GameState, content: Content, civId: Id, amount: number): boolean {
  const ai = weightsFor(content, state.civs[civId]);
  const spare = budget(state, content, civId, 0).net + paymentBalance(state, civId);
  return amount <= Math.max(0, spare) * ai.gptIncomeShare;
}

/** What a technology is worth in gold: what it costs to research. */
export function techValue(content: Content, tech: number): number {
  return techCost(content, tech);
}

/** Where an attitude falls among the bands, worst first: 0 Furious … 4 Friendly. */
export function bandRank(content: Content, score: number): number {
  const bands = content.rules.diplomacy.attitude.bands;
  const i = bands.findIndex((band) => band.below !== null && score < band.below);
  return i < 0 ? bands.length - 1 : i;
}

function unlocksSoldiers(content: Content, tech: number): boolean {
  return content.units.all.some((u) => u.techId === tech && u.attack > 0);
}

/**
 * Whether having this resource would let a civilisation build a soldier it cannot build
 * now: one it has the technology for, needing this and nothing else it lacks.
 */
export function resourceArms(state: GameState, content: Content, civId: Id, resource: number): boolean {
  const civ = state.civs[civId];
  if (!civ) return false;
  const access = resourceAccess(state, content, civId);
  const has = access.own | access.traded | (1 << resource);
  return content.units.all.some(
    (u) =>
      u.attack > 0 &&
      u.resourceIds.includes(resource) &&
      u.resourceIds.every((r) => (has & (1 << r)) !== 0) &&
      techBlocker(content, civ, { kind: 'unit', type: u.id }) === null,
  );
}

/**
 * How `judge` sees a deal with `other`: it would get `gets` and give `gives`.
 * `demand` means it is being asked to give something for nothing under threat.
 */
export function appraise(
  state: GameState,
  content: Content,
  judge: Id,
  other: Id,
  gets: DealItem[],
  gives: DealItem[],
  demand = false,
): Appraisal {
  const civ = state.civs[judge]!;
  const ai = weightsFor(content, civ);
  const turns = content.rules.diplomacy.paymentTurns;
  const mood = attitude(state, content, judge, other);
  const ours = militaryStrength(state, content, judge);
  const theirs = militaryStrength(state, content, other);
  const refuse = (reason: string): Appraisal => ({ gain: 0, loss: 0, accept: false, reason, shortfall: 0 });

  if (demand) {
    const loss = gives.reduce((sum, item) => sum + (item.kind === 'gold' ? item.amount : item.kind === 'tech' ? techValue(content, item.tech) : 0), 0);
    if (theirs < ai.yieldStrengthRatio * Math.max(1, ours)) return { ...refuse('They do not fear you enough to pay'), loss };
    return { gain: 0, loss, accept: true, reason: 'They give in rather than risk war', shortfall: 0 };
  }

  let gain = 0;
  let loss = 0;
  for (const [list, receiving] of [
    [gets, true],
    [gives, false],
  ] as const) {
    for (const item of list) {
      switch (item.kind) {
        case 'tech': {
          const value = techValue(content, item.tech);
          if (receiving) gain += value;
          else {
            // Giving knowledge away costs nothing directly; it is the rival's gain that hurts,
            // and most of all when it arms a stronger one.
            const danger = theirs > ours && unlocksSoldiers(content, item.tech) ? ai.dangerTechFactor : 1;
            loss += value * 0.5 * danger;
          }
          break;
        }
        case 'gold':
          if (receiving) gain += item.amount;
          else loss += item.amount;
          break;
        case 'goldPerTurn':
          // Promised gold is worth less than gold in hand; owed gold is owed in full.
          if (receiving) gain += item.amount * turns * 0.75;
          else {
            if (!canPromise(state, content, judge, item.amount)) return refuse('They cannot promise that much gold every turn');
            loss += item.amount * turns;
          }
          break;
        case 'openBorders':
          if (bandRank(content, mood.score) < 2) return refuse('They do not trust you enough to open their borders');
          if (ai.openBordersValue >= 0) gain += ai.openBordersValue;
          else loss -= ai.openBordersValue;
          break;
        case 'resource': {
          if (receiving) {
            const perTurn = resourceArms(state, content, judge, item.resource)
              ? content.rules.diplomacy.resourceValuePerTurn
              : content.rules.diplomacy.resourceIdlePerTurn;
            gain += perTurn * turns * 0.75;
          } else {
            const danger = theirs > ours && resourceArms(state, content, other, item.resource) ? ai.dangerTechFactor : 1;
            loss += content.rules.diplomacy.resourceCostPerTurn * turns * danger;
          }
          break;
        }
        case 'city':
          // Never in a peace-time deal (dealBlocker refuses it); priced in peace.ts.
          break;
        case 'alliance': {
          const enemy = item.against;
          const already = atWar(state, judge, enemy);
          if (!already) {
            if (bandRank(content, attitude(state, content, judge, enemy).score) >= 3) return refuse(`They will not turn on ${state.civs[enemy]?.name ?? 'a friend'}`);
            if (treatyLeft(state, judge, enemy) > 0) return refuse(`They will not break their treaty with ${state.civs[enemy]?.name ?? 'them'}`);
            const others = state.relations.some((r) => r.war && (r.a === judge || r.b === judge));
            if (others) return refuse('They will not fight two wars at once');
            const enemyStrength = militaryStrength(state, content, enemy);
            if (ours + theirs < ai.warStrengthRatio * Math.max(1, enemyStrength)) {
              return refuse(`They will not join a war against ${state.civs[enemy]?.name ?? 'them'} they cannot win`);
            }
          }
          gain += ai.allianceValue * (already ? 1.5 : 1);
          break;
        }
      }
    }
  }

  const margin = mood.band.dealMargin;
  if (margin === null) return { gain, loss, accept: false, reason: `They are ${mood.band.name.toLowerCase()} and will not deal with you`, shortfall: 0 };
  if (gives.length === 0) return { gain, loss, accept: true, reason: 'A gift is always welcome', shortfall: 0 };
  const needed = loss * margin;
  if (gain >= needed) return { gain, loss, accept: true, reason: 'They accept', shortfall: 0 };
  const shortfall = Math.ceil(needed - gain);
  return { gain, loss, accept: false, reason: `They want more — about ${shortfall} gold more`, shortfall };
}

export function transfer(state: GameState, content: Content, giver: Civ, receiver: Civ, item: DealItem, events: GameEvent[]): void {
  switch (item.kind) {
    case 'city': {
      const city = state.cities[item.city];
      if (city && city.owner === giver.id) events.push(...cedeCity(state, content, city, receiver.id));
      break;
    }
    case 'tech':
      if (!receiver.techs.includes(item.tech)) {
        receiver.techs.push(item.tech);
        receiver.techs.sort((p, q) => p - q);
      }
      if (receiver.researching === item.tech) receiver.researching = NONE;
      if (receiver.researchGoal === item.tech) receiver.researchGoal = NONE;
      events.push({ t: 'TechDiscovered', civId: receiver.id, tech: item.tech, name: content.technologies.get(item.tech).name });
      break;
    case 'gold': {
      const amount = Math.min(item.amount, giver.treasury);
      giver.treasury -= amount;
      receiver.treasury += amount;
      break;
    }
    case 'goldPerTurn':
      state.payments.push({
        id: state.nextId++,
        from: giver.id,
        to: receiver.id,
        amount: item.amount,
        until: state.meta.turn + content.rules.diplomacy.paymentTurns,
      });
      break;
    case 'openBorders':
      ensureRelation(state, giver.id, receiver.id).openBorders = true;
      break;
    case 'alliance': {
      const pact = ensureRelation(state, giver.id, receiver.id);
      if (!pact.alliances.includes(item.against)) {
        pact.alliances.push(item.against);
        pact.alliances.sort((p, q) => p - q);
      }
      for (const side of [giver.id, receiver.id]) {
        if (!atWar(state, side, item.against)) events.push(...goToWar(state, content, side, item.against));
      }
      remember(state, giver.id, receiver.id, 'allied');
      remember(state, receiver.id, giver.id, 'allied');
      events.push({ t: 'AllianceMade', a: giver.id, b: receiver.id, against: item.against });
      break;
    }
    case 'resource':
      state.grants.push({
        id: state.nextId++,
        from: giver.id,
        to: receiver.id,
        resource: item.resource,
        until: state.meta.turn + content.rules.diplomacy.paymentTurns,
      });
      break;
  }
}

/**
 * Declare war, breaking a treaty if one stands — which every civilisation hears about.
 * The one way war is declared, by the player, by the AI and by an alliance.
 */
export function goToWar(state: GameState, content: Content, by: Id, on: Id): GameEvent[] {
  const events: GameEvent[] = [];
  if (treatyLeft(state, by, on) > 0) {
    damageReputation(state, by, content.rules.diplomacy.reputation.brokeTreaty);
    events.push({ t: 'TreatyBroken', by, with: on });
  }
  events.push(...declareWar(state, by, on));
  return events;
}

/** Carry out a deal: `from` gives `give` and receives `get`. */
export function executeDeal(
  state: GameState,
  content: Content,
  from: Id,
  to: Id,
  give: DealItem[],
  get: DealItem[],
  demand: boolean,
): GameEvent[] {
  const a = state.civs[from]!;
  const b = state.civs[to]!;
  const events: GameEvent[] = [];
  let value = 0;
  for (const item of give) {
    transfer(state, content, a, b, item, events);
    value += item.kind === 'gold' ? item.amount : item.kind === 'tech' ? techValue(content, item.tech) : 0;
  }
  for (const item of get) transfer(state, content, b, a, item, events);
  if (demand) {
    remember(state, from, to, 'tributeReceived');
    remember(state, to, from, 'tributePaid');
  } else {
    remember(state, from, to, 'dealMade');
    remember(state, to, from, 'dealMade');
    if (get.length === 0 && value > 0) remember(state, to, from, 'gift', Math.min(15, value / 25));
  }
  events.unshift({ t: 'DealMade', from, to, give, get, demand });
  return events;
}

/** A refused deal: a refused demand is remembered by whoever made it. */
export function refuseDeal(state: GameState, by: Id, to: Id, reason: string, demand: boolean): GameEvent[] {
  if (demand) remember(state, to, by, 'demandRefused');
  return [{ t: 'DealRefused', by, to, reason, demand }];
}

/**
 * Pay what is owed this turn. A payment that has run its term ends quietly; one the
 * payer cannot meet ends in default, which costs it reputation with everyone.
 */
export function processPayments(state: GameState, content: Content, civId: Id): GameEvent[] {
  const events: GameEvent[] = [];
  const civ = state.civs[civId];
  if (!civ) return events;
  const kept = [];
  for (const payment of state.payments) {
    if (payment.from !== civId) {
      kept.push(payment);
      continue;
    }
    if (state.meta.turn >= payment.until) {
      events.push({ t: 'PaymentStopped', from: payment.from, to: payment.to, defaulted: false });
      continue;
    }
    const receiver = state.civs[payment.to];
    if (!receiver || receiver.defeated) continue;
    if (civ.treasury < payment.amount) {
      damageReputation(state, civId, content.rules.diplomacy.reputation.defaulted);
      events.push({ t: 'PaymentStopped', from: payment.from, to: payment.to, defaulted: true });
      continue;
    }
    civ.treasury -= payment.amount;
    receiver.treasury += payment.amount;
    kept.push(payment);
  }
  state.payments = kept;
  return events;
}

/**
 * End the resource deals a civilisation is giving that have run their term, or whose
 * resource it no longer has to give — a lost mine ends the trade, not the friendship.
 */
export function processGrants(state: GameState, content: Content, civId: Id): GameEvent[] {
  const events: GameEvent[] = [];
  const mine = state.grants.filter((g) => g.from === civId);
  if (mine.length === 0) return events;
  const own = resourceAccess(state, content, civId).own;
  state.grants = state.grants.filter((grant) => {
    if (grant.from !== civId) return true;
    const expired = state.meta.turn >= grant.until;
    const lost = (own & (1 << grant.resource)) === 0;
    const receiver = state.civs[grant.to];
    if (!expired && !lost && receiver && !receiver.defeated) return true;
    events.push({ t: 'ResourceGrantEnded', from: grant.from, to: grant.to, resource: grant.resource, lost: !expired && lost });
    return false;
  });
  return events;
}

/** Gold a civilisation pays (negative) and receives (positive) a turn under deals. */
export function paymentBalance(state: GameState, civId: Id): number {
  let net = 0;
  for (const p of state.payments) {
    if (p.from === civId) net -= p.amount;
    if (p.to === civId) net += p.amount;
  }
  return net;
}

/** Withdraw offers nobody answered in time. An unanswered demand counts as refused. */
export function expireOffers(state: GameState): GameEvent[] {
  const events: GameEvent[] = [];
  state.offers = state.offers.filter((offer) => {
    if (offer.expires > state.meta.turn) return true;
    if (offer.demand) remember(state, offer.from, offer.to, 'demandRefused');
    events.push({ t: 'OfferWithdrawn', offerId: offer.id, from: offer.from, to: offer.to });
    return false;
  });
  return events;
}
